import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Audience } from '@epasu/core';
import { Unlink, UserPlus, UserSearch } from 'lucide-react';
import { crm, type ContactRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AssignContactDialog } from '@/components/AssignContactDialog';
import { ContactDialog } from '@/components/ContactDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';

interface Props {
  organizationId: string;
  organizationName: string;
  defaultAudienceId: string;
  contacts: ContactRow[];
  audiences: Audience[];
  onChanged: () => void;
}

/**
 * Ansprechpartner einer Firma: anlegen, vorhandene zuordnen, lösen.
 *
 * Lädt selbst nichts — jede Änderung meldet sich über `onChanged` an die
 * Firmenakte zurück, die als einzige Stelle lädt.
 */
export function ContactsTab({
  organizationId,
  organizationName,
  defaultAudienceId,
  contacts,
  audiences,
  onChanged,
}: Props) {
  const navigate = useNavigate();
  const [neuerKontakt, setNeuerKontakt] = useState(false);
  const [zuordnen, setZuordnen] = useState(false);
  const [loesen, setLoesen] = useState<ContactRow | null>(null);

  return (
    <>
      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Ansprechpartner</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setZuordnen(true)}>
              <UserSearch className="mr-2 h-4 w-4" />
              Vorhandene zuordnen
            </Button>
            <Button variant="outline" size="sm" onClick={() => setNeuerKontakt(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Neue Person
            </Button>
          </div>
        </div>

        {contacts.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Noch kein Ansprechpartner hinterlegt. „Vorhandene zuordnen“ hängt eine
            Person an, die es schon gibt.
          </p>
        ) : (
          <ul className="mt-3 divide-y">
            {contacts.map((c) => (
              <li key={c.id} className="group flex items-center gap-2">
                <button
                  className="flex min-w-0 flex-1 items-center justify-between gap-3 py-2.5 text-left hover:bg-muted/30"
                  onClick={() => navigate(`/kontakte/${c.id}`)}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {c.displayName}
                      {c.optedOut && (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-normal text-amber-900">
                          abgemeldet
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {[c.role, c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                </button>
                <button
                  className="shrink-0 rounded p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                  title={`${c.displayName} von dieser Firma lösen`}
                  aria-label={`${c.displayName} von dieser Firma lösen`}
                  onClick={() => setLoesen(c)}
                >
                  <Unlink className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {neuerKontakt && (
        <ContactDialog
          contact={null}
          audiences={audiences}
          defaultOrganizationName={organizationName}
          defaultAudienceId={defaultAudienceId}
          onClose={() => setNeuerKontakt(false)}
          onSaved={() => {
            setNeuerKontakt(false);
            onChanged();
          }}
        />
      )}

      {zuordnen && (
        <AssignContactDialog
          organizationId={organizationId}
          organizationName={organizationName}
          onClose={() => setZuordnen(false)}
          // Offen lassen: bei einer Schule hängt man meist mehrere
          // Ansprechpartner nacheinander an, und jedes Mal neu öffnen hält auf.
          onAssigned={onChanged}
        />
      )}

      {loesen && (
        <ConfirmDialog
          title={`${loesen.displayName} von ${organizationName} lösen?`}
          confirmLabel="Lösen"
          onCancel={() => setLoesen(null)}
          onConfirm={async () => {
            await crm.contacts.setOrganization(loesen.id, null);
            setLoesen(null);
            onChanged();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Der Kontakt bleibt bestehen und gehört danach zu keiner Firma. Sein
            Schriftverkehr und seine Deals bleiben an ihm hängen.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}
